import React, { useEffect, useState } from "react";
import { Grid, TextField, Autocomplete } from "@mui/material";
import { Button } from "antd";
import AdvancedTable from "../../sharedComponents/AdvancedTable/AdvancedTable";
import { getIssueWiseReport, downloadIssueWiseReport } from "../../services/reports.service";
import { getUser } from "../../services/user.service";
import { getAllProject } from "../../services/reports.service";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { ISSUETYPE } from "../../common/constant/constant";
import ClientTaskDetailsDialog from "./Components/ClientTaskDetailsDialog";

const { RangePicker } = DatePicker;

const IssueWiseReport = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogData, setDialogData] = useState({
        projectId: null,
        projectName: "",
        userId: null,
        issueType: null,
        startDate: null,
        endDate: null,
    });

    const initialMonth = dayjs();
    const [dateRange, setDateRange] = useState({
        startDate: initialMonth.startOf("month"),
        endDate: initialMonth,
    });
    const [data, setData] = useState([]);

    const [payableFilter, setPayableFilter] = useState("All");
    const payableOptions = [
        { label: "All", value: "All" },
        { label: "Payable", value: "true" },
        { label: "Unpayable", value: "false" },
    ];

    // Issue type labels mapping
    const issueTypeLabels = {
        [ISSUETYPE.BUG]: "Bug",
        [ISSUETYPE.TASK]: "Task",
        [ISSUETYPE.TESTING]: "Testing",
        [ISSUETYPE.DESIGNING]: "Designing",
        [ISSUETYPE.OTHERS]: "Others",
        [ISSUETYPE.MEETING]: "Meeting",
        [ISSUETYPE.DEPLOYMENT]: "Deployment",
    };

    // Reverse mapping: label -> issueType value
    const labelToIssueType = Object.entries(issueTypeLabels).reduce((acc, [key, label]) => {
        acc[label] = parseInt(key);
        return acc;
    }, {});

    // Fetch all users
    useEffect(() => {
        getUser({ isEnabled: true }).then((res) => {
            const userList = Array.isArray(res.data?.data) ? res.data.data : [];
            setUsers([{ _id: "All", name: "All" }, ...userList]);
        });
    }, []);

    // Fetch projects
    useEffect(() => {
        getAllProject({ isEnabled: true }).then((res) => {
            const projectList = Array.isArray(res.data) ? res.data : [];
            setProjects(projectList);
        });
    }, []);

    // Fetch report whenever filters change
    useEffect(() => {
        const fetchReport = async () => {
            setLoading(true);
            try {
                const projectIds = selectedProjects.length > 0
                    ? selectedProjects.map(p => p._id).join(',')
                    : null;

                const res = await getIssueWiseReport({
                    startDate: dateRange.startDate
                        ? dateRange.startDate.startOf("day").toISOString()
                        : null,
                    endDate: dateRange.endDate
                        ? dateRange.endDate.endOf("day").toISOString()
                        : null,
                    userId: selectedUser?._id || "All",
                    projectIds: projectIds,
                    payable: payableFilter === "All" ? null : payableFilter,
                });
                const reportData = Array.isArray(res.data) ? res.data : [];
                setData(reportData);
            } catch (error) {
                console.error("Error fetching issue wise report:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchReport();
    }, [selectedUser, dateRange, selectedProjects, payableFilter]);

    const handleCellClick = (row, column) => {
        // Only handle clicks on issue type columns (not projectName or Total)
        if (column.isClickable && row[column.id] !== "0.00") {
            // Get the issue type value from the column label
            const issueTypeValue = labelToIssueType[column.id];

            setDialogData({
                projectId: row.projectId,
                projectName: row.projectName,
                userId: selectedUser?._id || "All",
                issueType: issueTypeValue,
                startDate: dateRange.startDate ? dateRange.startDate.toISOString() : null,
                endDate: dateRange.endDate ? dateRange.endDate.toISOString() : null,
            });
            setDialogOpen(true);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setDialogData({
            projectId: null,
            projectName: "",
            userId: null,
            issueType: null,
            startDate: null,
            endDate: null,
        });
    };

    // Prepare table columns
    const columns = [
        { id: "projectName", label: "Project Name" },
        { id: "Bug", label: "Bug", isTotal: true, totalType: "time", isClickable: true },
        { id: "Task", label: "Task", isTotal: true, totalType: "time", isClickable: true },
        { id: "Testing", label: "Testing", isTotal: true, totalType: "time", isClickable: true },
        { id: "Designing", label: "Designing", isTotal: true, totalType: "time", isClickable: true },
        { id: "Others", label: "Others", isTotal: true, totalType: "time", isClickable: true },
        { id: "Meeting", label: "Meeting", isTotal: true, totalType: "time", isClickable: true },
        { id: "Deployment", label: "Deployment", isTotal: true, totalType: "time", isClickable: true },
        { id: "Total", label: "Total Hours", isTotal: true, isTotalColumn: true, totalType: "time" },
    ];

    const transformedData = data.map((project) => {
        const row = {
            projectName: project.projectName,
            projectId: project.projectId || project._id
        };
        let totalMinutes = 0;

        // Add each issue type
        Object.keys(ISSUETYPE).forEach((key) => {
            const issueTypeValue = ISSUETYPE[key];
            const issueTypeHours = project.issueTypes[issueTypeValue] || 0.0;

            const hours = Math.floor(issueTypeHours);
            const mins = Math.round((issueTypeHours % 1) * 100);
            totalMinutes += hours * 60 + mins;

            row[issueTypeLabels[issueTypeValue]] = `${hours}.${mins.toString().padStart(2, "0")}`;
        });

        const totalHours = Math.floor(totalMinutes / 60);
        const totalMins = totalMinutes % 60;
        row.Total = `${totalHours}.${totalMins.toString().padStart(2, "0")}`;

        return row;
    });
    transformedData.sort((a, b) => a.projectName.localeCompare(b.projectName));

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const projectIds = selectedProjects.length > 0
                ? selectedProjects.map(p => p._id).join(',')
                : null;

            const response = await downloadIssueWiseReport({
                startDate: dateRange.startDate
                    ? dateRange.startDate.startOf("day").toISOString()
                    : null,
                endDate: dateRange.endDate
                    ? dateRange.endDate.endOf("day").toISOString()
                    : null,
                userId: selectedUser?._id || "All",
                projectIds: projectIds,
                payable: payableFilter === "All" ? null : payableFilter,
            });

            // Handle blob response
            const blob = new Blob([response], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            const startStr = dateRange.startDate.format("YYYY-MM-DD");
            const endStr = dateRange.endDate.format("YYYY-MM-DD");
            link.setAttribute("download", `Issue_Wise_Report_${startStr}_to_${endStr}.xlsx`);

            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            console.error("Error downloading report:", error);
            alert("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const tableConfig = {
        isAction: false,
        isEdit: false,
        isMore: false,
        isAdmin: false,
    };

    return (
        <div className="app-container">
            <Grid container spacing={2}>
                <Grid item>
                    <RangePicker
                        value={[
                            dateRange.startDate ? dayjs(dateRange.startDate) : null,
                            dateRange.endDate ? dayjs(dateRange.endDate) : null,
                        ]}
                        onChange={(dates) => {
                            const [start, end] = dates || [];
                            if (start && end) {
                                setDateRange({
                                    startDate: dayjs(start).startOf("day"),
                                    endDate: dayjs(end).endOf("day"),
                                });
                            } else {
                                const initialMonth = dayjs();
                                setDateRange({
                                    startDate: initialMonth.startOf("month"),
                                    endDate: initialMonth.endOf("month"),
                                });
                            }
                        }}
                        className="datePicker w-100"
                        format={"DD-MM-YYYY"}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        options={users}
                        getOptionLabel={(u) => u.name || ""}
                        value={selectedUser}
                        onChange={(e, val) => setSelectedUser(val)}
                        renderInput={(params) => (
                            <TextField {...params} label="Select User" />
                        )}
                        sx={{ width: 200 }}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        multiple
                        limitTags={2}
                        disableCloseOnSelect
                        options={projects}
                        getOptionLabel={(p) => p.name || ""}
                        value={selectedProjects}
                        onChange={(e, val) => setSelectedProjects(val)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Projects"
                                placeholder={selectedProjects.length === 0 ? "Select Projects" : ""}
                            />
                        )}
                        sx={{
                            width: 200,
                        }}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        options={payableOptions}
                        getOptionLabel={(option) => option.label}
                        value={payableOptions.find((opt) => opt.value === payableFilter) || payableOptions[0]}
                        onChange={(e, val) => setPayableFilter(val ? val.value : "All")}
                        renderInput={(params) => <TextField {...params} label="Payable" />}
                        sx={{ width: 150 }}
                        disableClearable
                    />
                </Grid>
                <Grid item>
                    <Button
                        disabled={loading}
                        type="primary"
                        onClick={handleDownload}
                        loading={downloadLoading}
                        style={{
                            backgroundColor: "#008080",
                            borderColor: "#008080",
                            height: "40px",
                        }}
                    >
                        Download Excel
                    </Button>
                </Grid>
            </Grid>
            <AdvancedTable
                columns={columns}
                data={transformedData}
                tableConfig={tableConfig}
                isPagination={false}
                openLoader={loading}
                onCellClick={handleCellClick}
            />

            <ClientTaskDetailsDialog
                open={dialogOpen}
                handleClose={handleDialogClose}
                data={dialogData}
            />
        </div>
    );
};

export default IssueWiseReport;
